import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { Cart } from "@insite/client-framework/Services/CartService";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import { PromotionModel } from "@insite/client-framework/Types/ApiModels";
import LocalizedCurrency from "@insite/content-library/Components/LocalizedCurrency";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { getIsUkWebsite } from "@tSurya/Common/Utilities/websiteUtils";
import Constant from "@tSurya/Constants/Constant";
import CustomApplicationState from "@tSurya/Store/Data/CustomApplicationState/CustomApplicationState";
import {
    parseBrandPrefixedAmounts,
    ShippingLocationLabels,
} from "@tSurya/Common/Utilities/websiteUtils";
import {
    groupPromotionResults,
    isItemDiscountPromotion,
    isShippingPromotion,
} from "@tSurya/Common/Utilities/promotionDisplay";
import { COLORS, TYPOGRAPHY, WIDGETS } from "@tSurya/theme/design";
import React, { Fragment } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    isLoading?: boolean;
    /**
     * If true, displays an empty cart total UI.
     * If false, displays amounts from the cart.
     * Default value: false
     */
    isCartEmpty?: boolean;
    /**
     * If true, displays tax, shipping and handling, and total amounts.
     * If false, does not display tax, shipping and handling, or total amounts.
     * Default value: true
     */
    showTaxAndShipping?: boolean;
    /**
     * If true, displays item count in the subtotal label (e.g., "Subtotal (5 items)").
     * Default value: false
     */
    showItemCountInSubtotal?: boolean;
    cart?: Cart;
    orderPromotions?: PromotionModel[];
    shippingPromotions?: PromotionModel[];
    allPromotions?: PromotionModel[];
    discountTotal?: number;
    fulfillmentMethod?: string;
    extendedStyles?: CartTotalDisplayStyles;
}

const mapStateToProps = (state: CustomApplicationState, ownProps: OwnProps) => {
    const shipToId = ownProps.cart?.shipToId;
    const shipToCountryAbbreviation = getShipToState(state, shipToId).value?.country?.abbreviation?.toUpperCase() ?? "";

    return {
        enableVat: getSettingsCollection(state).productSettings.enableVat,
        currentPage: getCurrentPage(state).type,
        showPricing: state.context.showPricing,
        isCheckoutReviewAndSubmitPage: getCurrentPage(state).type === "CheckoutReviewAndSubmitPage",
        isUkWebsite: getIsUkWebsite(state),
        shipToCountryAbbreviation,
        locationLabels: {
            suryaShippingLocation: (state.context.settings.properties.suryaShippingLocation as string) || "Georgia",
            globalViewsShippingLocation:
                (state.context.settings.properties.globalViewsShippingLocation as string) || "Texas",
            mgbwShippingLocation: (state.context.settings.properties.mgbwShippingLocation as string) || "North Carolina",
        } as ShippingLocationLabels,
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface CartTotalDisplayStyles {
    container?: GridContainerProps;
    totalContainer?: GridContainerProps;
    labelGridItem?: GridItemProps;
    totalGridItem?: GridItemProps;
    valueGridItem?: GridItemProps;
    totalValueGridItem?: GridItemProps;
    subtotalLabel?: TypographyProps;
    subtotalValue?: TypographyProps;
    promotionLabel?: TypographyProps;
    promotionValue?: TypographyProps;
    discountPromotionLabel?: TypographyProps;
    discountPromotionValue?: TypographyProps;
    shippingPromotionMessage?: TypographyProps;
    shippingPromotionCalculationNote?: TypographyProps;
    productSavingsMessage?: TypographyProps;
    discountsLabel?: TypographyProps;
    discountsValue?: TypographyProps;
    shippingAndHandlingLabel?: TypographyProps;
    shippingAndHandlingValue?: TypographyProps;
    whiteGloveLabel?: TypographyProps;
    whiteGloveValue?: TypographyProps;
    otherChargesLabel?: TypographyProps;
    otherChargesValue?: TypographyProps;
    taxLabel?: TypographyProps;
    taxValue?: TypographyProps;
    taxMessageNote?: TypographyProps;
    totalLabel?: TypographyProps;
    totalValue?: TypographyProps;
    estimatedTotalGridItem?: GridItemProps;
    estimatedTotalLabel?: TypographyProps;
    estimatedTotalValue?: TypographyProps;
    estimatedTotalHelperText?: TypographyProps;
    loadingSpinner?: LoadingSpinnerProps;
    horizontalLineStyles?: InjectableCss;
    totalText?: TypographyProps;
    totalValueText?: TypographyProps;
}

const promotionSummaryTokens = (WIDGETS as any).promotionSummary;

export const cartTotalDisplayStyles: CartTotalDisplayStyles = {
    container: {
        gap: 10,
        css: css`
            background-color: ${COLORS.checkout.lightBg};
            padding: 20px;
            font-family: ${promotionSummaryTokens?.fontFamily};
            > div[class^="GridOffset"] {
                > div[class^="GridItemStyle"]:last-of-type {
                    justify-content: flex-end;
                }
            }
        `,
    },
    labelGridItem: { width: 6 },
    totalGridItem: {
        width: 6,
        css: css``,
    },
    valueGridItem: { width: 6 },
    totalValueGridItem: {
        width: 6,
        css: css``,
    },
    taxLabel: {
        css: css`
            color: ${COLORS.text.main};
            font-family: ${promotionSummaryTokens?.fontFamily};
            font-size: 15px;
            font-weight: ${TYPOGRAPHY.weight.base};
        `,
    },
    taxMessageNote: {
        css: css`
            font-style: italic;
            font-family: ${promotionSummaryTokens?.fontFamily};
            font-size: 13px;
            color: #777;
            text-align: right;
            width: 100%;
            white-space: pre-line;
        `,
    },
    subtotalValue: {
        css: css`
            margin-left: auto;
            color: ${COLORS.text.main};
            font-family: ${promotionSummaryTokens?.fontFamily};
            font-size: 15px;
            font-weight: ${TYPOGRAPHY.weight.base};
        `,
    },
    promotionValue: {
        css: css`
            margin-left: auto;
            color: ${promotionSummaryTokens?.savingsColor};
            font-family: ${promotionSummaryTokens?.fontFamily};
            font-size: ${promotionSummaryTokens?.discountValueFontSize};
            font-weight: ${TYPOGRAPHY.weight.base};
        `,
    },
    discountPromotionLabel: {
        css: css`
            color: ${COLORS.text.main};
            font-family: ${promotionSummaryTokens?.fontFamily};
            font-size: ${promotionSummaryTokens?.discountFontSize};
            font-weight: ${TYPOGRAPHY.weight.base};
        `,
    },
    discountPromotionValue: {
        css: css`
            margin-left: auto;
            color: ${promotionSummaryTokens?.savingsColor};
            font-family: ${promotionSummaryTokens?.fontFamily};
            font-size: ${promotionSummaryTokens?.discountValueFontSize};
            font-weight: ${TYPOGRAPHY.weight.base};
        `,
    },
    shippingPromotionMessage: {
        css: css`
            font-style: italic;
            font-family: ${promotionSummaryTokens?.fontFamily};
            font-size: ${promotionSummaryTokens?.messageFontSize};
            color: ${promotionSummaryTokens?.messageColor};
            width: 100%;
            text-align: right;
            white-space: ${promotionSummaryTokens?.messageWhiteSpace};
        `,
    },
    shippingPromotionCalculationNote: {
        css: css`
            font-family: ${promotionSummaryTokens?.fontFamily};
            font-size: ${promotionSummaryTokens?.messageFontSize};
            color: ${promotionSummaryTokens?.messageColor};
            text-align: left;
            width: 100%;
        `,
    },
    productSavingsMessage: {
        css: css`
            font-family: ${promotionSummaryTokens?.fontFamily};
            font-size: 14px;
            font-weight: 500;
            color: #2E7D32;
            width: 100%;
        `,
    },
    discountsValue: {
        css: css`
            margin-left: auto;
            color: ${COLORS.text.main};
            font-family: ${promotionSummaryTokens?.fontFamily};
            font-size: 15px;
            font-weight: ${TYPOGRAPHY.weight.base};
        `,
    },
    shippingAndHandlingValue: {
        css: css`
            margin-left: auto;
            color: ${COLORS.text.main};
            font-family: ${promotionSummaryTokens?.fontFamily};
            font-size: 15px;
            font-weight: ${TYPOGRAPHY.weight.base};
        `,
    },
    otherChargesValue: {
        css: css`
            margin-left: auto;
            color: ${COLORS.text.main};
            font-family: ${promotionSummaryTokens?.fontFamily};
            font-size: 15px;
            font-weight: ${TYPOGRAPHY.weight.base};
        `,
    },
    taxValue: {
        css: css`
            margin-left: auto;
            color: ${COLORS.text.main};
            font-family: ${promotionSummaryTokens?.fontFamily};
            font-size: 15px;
            font-weight: ${TYPOGRAPHY.weight.base};
        `,
    },
    totalLabel: {
        weight: "bold",
        css: css`
            color: ${COLORS.text.main};
            font-family: ${promotionSummaryTokens?.fontFamily};
            font-style: normal;
            font-weight: ${TYPOGRAPHY.weight.bold};
            font-size: 15px;
            line-height: ${TYPOGRAPHY.lineHeight.xbase};
        `,
    },
    totalValue: {
        weight: "bold",
        css: css`
            margin-left: auto;
            color: ${COLORS.text.main};
            font-family: ${promotionSummaryTokens?.fontFamily};
            font-style: normal;
            font-weight: ${TYPOGRAPHY.weight.bold};
            font-size: 15px;
            line-height: ${TYPOGRAPHY.lineHeight.base};
        `,
    },
    estimatedTotalGridItem: {
        width: 6,
        css: css`
            flex-direction: column;
            align-items: flex-start;
        `,
    },
    estimatedTotalLabel: {
        weight: "bold",
        css: css`
            color: ${COLORS.text.main};
            font-family: ${promotionSummaryTokens?.fontFamily};
            font-size: ${promotionSummaryTokens?.estimatedTotalFontSize};
            font-weight: ${promotionSummaryTokens?.estimatedTotalFontWeight};
            line-height: ${promotionSummaryTokens?.estimatedTotalLineHeight};
        `,
    },
    estimatedTotalValue: {
        weight: "bold",
        css: css`
            margin-left: auto;
            color: ${COLORS.text.main};
            font-family: ${promotionSummaryTokens?.fontFamily};
            font-size: ${promotionSummaryTokens?.estimatedTotalFontSize};
            font-weight: ${promotionSummaryTokens?.estimatedTotalFontWeight};
            line-height: ${promotionSummaryTokens?.estimatedTotalLineHeight};
        `,
    },
    estimatedTotalHelperText: {
        css: css`
            margin-top: ${promotionSummaryTokens?.estimatedTotalHelperMarginTop};
            color: ${promotionSummaryTokens?.messageColor};
            font-family: ${promotionSummaryTokens?.fontFamily};
            font-size: ${promotionSummaryTokens?.estimatedTotalHelperFontSize};
            line-height: ${promotionSummaryTokens?.estimatedTotalHelperLineHeight};
        `,
    },
    loadingSpinner: {
        size: 15,
        css: css`
            margin-top: auto;
            margin-bottom: auto;
            margin-left: auto;
        `,
    },
    subtotalLabel: {
        css: css`
            margin-bottom: 20px;
            color: ${COLORS.text.main};
            font-family: ${promotionSummaryTokens?.fontFamily};
            font-style: normal;
            font-weight: ${TYPOGRAPHY.weight.base};
            font-size: 15px;
            line-height: ${TYPOGRAPHY.lineHeight.base};
            :last-child {
                margin-bottom: 0;
            }
        `,
    },
    promotionLabel: {
        css: css`
            color: ${COLORS.text.main};
            font-family: ${promotionSummaryTokens?.fontFamily};
            font-size: ${promotionSummaryTokens?.discountFontSize};
            font-weight: ${TYPOGRAPHY.weight.base};
        `,
    },
    discountsLabel: {
        css: css`
            color: ${COLORS.text.main};
            font-family: ${promotionSummaryTokens?.fontFamily};
            font-size: 15px;
            font-weight: ${TYPOGRAPHY.weight.base};
        `,
    },
    shippingAndHandlingLabel: {
        css: css`
            color: ${COLORS.text.main};
            font-family: ${promotionSummaryTokens?.fontFamily};
            font-size: 15px;
            font-weight: ${TYPOGRAPHY.weight.base};
        `,
    },
    whiteGloveLabel: {
        css: css`
            color: ${COLORS.text.main};
            font-family: ${promotionSummaryTokens?.fontFamily};
            font-size: 15px;
            font-weight: ${TYPOGRAPHY.weight.base};
        `,
    },
    whiteGloveValue: {
        css: css`
            color: ${COLORS.text.main};
            font-family: ${promotionSummaryTokens?.fontFamily};
            font-size: 15px;
            font-weight: ${TYPOGRAPHY.weight.base};
            margin-left: auto;
        `,
    },
    otherChargesLabel: {
        css: css`
            color: ${COLORS.text.main};
            font-family: ${promotionSummaryTokens?.fontFamily};
            font-size: 15px;
            font-weight: ${TYPOGRAPHY.weight.base};
        `,
    },
    horizontalLineStyles: {
        css: css`
            width: 100%;
            height: 0;
            margin: ${promotionSummaryTokens?.dividerMargin};
            border-top: 1px solid ${promotionSummaryTokens?.dividerColor};
        `,
    },
    totalText: {
        weight: "bold",
        css: css`
            font-size: ${TYPOGRAPHY.size.large};
            font-family: ${promotionSummaryTokens?.fontFamily};
        `,
    },
    totalValueText: {
        weight: "bold",
        css: css`
            font-size: ${TYPOGRAPHY.size.large};
            font-family: ${promotionSummaryTokens?.fontFamily};
        `,
    },
};

const CartTotalDisplay = ({
    isCartEmpty = false,
    showTaxAndShipping = true,
    showItemCountInSubtotal = false,
    cart,
    orderPromotions,
    shippingPromotions,
    allPromotions,
    fulfillmentMethod,
    isLoading,
    enableVat,
    showPricing,
    isCheckoutReviewAndSubmitPage,
    extendedStyles,
    currentPage,
    isUkWebsite,
    shipToCountryAbbreviation,
    locationLabels,
}: Props) => {
    const [styles] = React.useState(() => mergeToNew(cartTotalDisplayStyles, extendedStyles));

    const getSubtotalLabel = () => {
        const itemCount = cart?.totalCountDisplay ?? 0;
        const itemsText = itemCount === 1 ? translate("item") : translate("items");
        const countText = showItemCountInSubtotal && itemCount > 0 ? ` (${itemCount} ${itemsText})` : "";
        return translate("Subtotal") + countText + (enableVat ? ` (${translate("Ex. VAT")})` : "");
    };

    if (!showPricing && !isCheckoutReviewAndSubmitPage) {
        return null;
    }

    if (isLoading || !cart) {
        return (
            <GridContainer {...styles.container}>
                {showTaxAndShipping && (
                    <>
                        <GridItem {...styles.labelGridItem}>
                            <Typography {...styles.totalLabel}>{translate("Total")}</Typography>
                        </GridItem>
                        <GridItem {...styles.valueGridItem}>
                            <LoadingSpinner {...styles.loadingSpinner} />
                        </GridItem>
                    </>
                )}
                {!showTaxAndShipping && (
                    <>
                        <GridItem {...styles.labelGridItem}>
                            <Typography {...styles.subtotalLabel}>{getSubtotalLabel()}</Typography>
                        </GridItem>
                        <GridItem {...styles.valueGridItem}>
                            <LoadingSpinner {...styles.loadingSpinner} />
                        </GridItem>
                    </>
                )}
            </GridContainer>
        );
    }

    if (isCartEmpty) {
        return (
            <GridContainer {...styles.container}>
                {showTaxAndShipping && (
                    <>
                        <GridItem {...styles.labelGridItem}>
                            <Typography {...styles.totalLabel}>{translate("Total")}</Typography>
                        </GridItem>
                        <GridItem {...styles.valueGridItem}>
                            <Typography {...styles.totalValue}>{`${cart.currencySymbol} -`}</Typography>
                        </GridItem>
                    </>
                )}
                {!showTaxAndShipping && (
                    <>
                        <GridItem {...styles.labelGridItem}>
                            <Typography {...styles.subtotalLabel}>{getSubtotalLabel()}</Typography>
                        </GridItem>
                        <GridItem {...styles.valueGridItem}>
                            <Typography {...styles.subtotalValue}>{`${cart.currencySymbol} -`}</Typography>
                        </GridItem>
                    </>
                )}
            </GridContainer>
        );
    }

    const RollpadServiceCount =
        cart.cartLines
            ?.filter(line => line.properties.isRollpadService === "true")
            .reduce((total, line) => total + (line.qtyOrdered ?? 0), 0) || 0;
    const rollpadServiceChargeAmount = RollpadServiceCount * Constant.rollServiceUnitPrice;
    const serviceCharge = `$${rollpadServiceChargeAmount.toFixed(2)}`;

    const isUkAllowedCountry = shipToCountryAbbreviation === "GB" || shipToCountryAbbreviation === "IE";
    const allowUkTaxAmount = isUkWebsite && isUkAllowedCountry;
    const allowTaxAmountOnPage = currentPage !== "CheckoutShippingPage";
    // Outside the UK, pickup tax is calculated from the pickup warehouse and shown like a shipped order.
    const allowTaxAmountByFulfillment =
        !isUkWebsite || fulfillmentMethod !== FulfillmentMethod.PickUp || allowUkTaxAmount;

    // White Glove is persisted per brand as `BrandWhiteGlove_{brand}` at checkout. Sum the
    // WHITE_GLOVE carrier cost across every persisted brand for a single order-summary line.
    const whiteGloveTotal = parseBrandPrefixedAmounts(cart.properties, "brandWhiteGlove_", locationLabels).reduce(
        (sum, entry) => sum + entry.amount,
        0,
    );

    // Base shipping is the order shipping minus any per-brand white glove cost, shown as a single
    // "Shipping & Handling" line.
    const baseShippingAndHandling = Math.max((cart.shippingAndHandling || 0) - whiteGloveTotal, 0);

    // On the cart and the shipping-address step (checkout step 1) the freight is not final yet,
    // so a shipping promo would show a stale/$0 discount. On those pages show an informational
    // message instead of the amount; on Review & Submit the freight is known so keep the value.
    const showShippingPromotionAsMessage =
        currentPage === "CartPage" || currentPage === "CheckoutShippingPage";
    const isCheckoutShippingAddressStep = currentPage === "CheckoutShippingPage";
    const promotionSource =
        allPromotions && allPromotions.length > 0
            ? allPromotions
            : [...(orderPromotions ?? []), ...(shippingPromotions ?? [])];
    const discountPromotionGroups = groupPromotionResults(promotionSource, isItemDiscountPromotion);
    const shippingPromotionGroups = groupPromotionResults(promotionSource, isShippingPromotion);
    const visibleShippingPromotionGroups = showShippingPromotionAsMessage
        ? shippingPromotionGroups
        : shippingPromotionGroups.filter(group => group.amount > 0);
    const isPreShippingEstimatedTotalContext =
        currentPage === "CartPage" || currentPage === "CheckoutShippingPage";
    const isFinalizedCheckoutSummary =
        isCheckoutReviewAndSubmitPage || currentPage === "OrderConfirmationPage";
    const showEstimatedTotal =
        currentPage === "CheckoutShippingPage" ||
        (currentPage === "CartPage" && discountPromotionGroups.length > 0);
    const showPrePromotionSubtotal =
        discountPromotionGroups.length > 0 &&
        (isPreShippingEstimatedTotalContext || isFinalizedCheckoutSummary);
    const hasVisiblePromotions =
        isPreShippingEstimatedTotalContext &&
        (discountPromotionGroups.length > 0 || visibleShippingPromotionGroups.length > 0);
    const showFinalizedPromotionBlock =
        isFinalizedCheckoutSummary &&
        (discountPromotionGroups.length > 0 || visibleShippingPromotionGroups.length > 0);
    const displayedNonShippingDiscountTotal = discountPromotionGroups.reduce(
        (total, promotion) => total + promotion.amount,
        0,
    );
    const estimatedTotal = Math.max(
        0,
        (cart.orderSubTotalWithOutProductDiscounts || 0) -
            displayedNonShippingDiscountTotal +
            rollpadServiceChargeAmount,
    );
    const showShippingPromotionCalculationNote = isCheckoutShippingAddressStep && shippingPromotionGroups.length > 0;
    const showTaxCalculationNote =
        isCheckoutShippingAddressStep &&
        showTaxAndShipping &&
        (!isUkWebsite || fulfillmentMethod !== FulfillmentMethod.PickUp);
    const pendingCalculationNote = showShippingPromotionCalculationNote && showTaxCalculationNote
        ? translate("Your shipping discount and taxes will be calculated once you select a shipping method at checkout.")
        : showShippingPromotionCalculationNote
        ? translate("Your shipping discount will be calculated once you select a shipping method at checkout.")
        : fulfillmentMethod === FulfillmentMethod.PickUp
        ? translate("Taxes will be calculated in the next step, based on your pick up location.")
        : translate("Taxes will be calculated once you select a shipping method at checkout.");
    return (
        <GridContainer {...styles.container} data-test-selector={`cartTotal_${cart.id}`}>
            <GridItem {...styles.labelGridItem}>
                <Typography {...styles.subtotalLabel}>{getSubtotalLabel()}</Typography>
            </GridItem>
            <GridItem {...styles.valueGridItem}>
                <Typography {...styles.subtotalValue} data-test-selector="cartTotal_subTotal">
                    {showPrePromotionSubtotal
                        ? cart.orderSubTotalWithOutProductDiscountsDisplay
                        : cart.orderSubTotalDisplay}
                </Typography>
            </GridItem>

            {RollpadServiceCount > 0 && (
                <>
                    <GridItem {...styles.labelGridItem}>
                        <Typography {...styles.subtotalLabel}>{translate("Roll Pad Service")}</Typography>
                    </GridItem>
                    <GridItem {...styles.valueGridItem}>
                        <Typography {...styles.subtotalValue} data-test-selector="cartTotal_subTotal">
                            {serviceCharge}
                        </Typography>
                    </GridItem>
                </>
            )}

            {showTaxAndShipping && isCheckoutShippingAddressStep && (
                <>
                    <GridItem {...styles.labelGridItem} width={3}>
                        <Typography {...styles.taxLabel}>{enableVat ? translate("VAT") : translate("Tax")}</Typography>
                    </GridItem>
                    <GridItem {...styles.valueGridItem} width={9}>
                        <Typography {...styles.taxMessageNote}>
                            {isUkWebsite && fulfillmentMethod === FulfillmentMethod.PickUp
                                ? siteMessage("CheckoutShipping_CartTotal_Pickup_Taxes")
                                : `${siteMessage("Promotion_ShippingDiscountCalculatedAtCheckout")}*`}
                        </Typography>
                    </GridItem>
                </>
            )}

            {hasVisiblePromotions && (
                <GridItem {...styles.valueGridItem} width={12}>
                    <StyledWrapper {...styles.horizontalLineStyles} />
                </GridItem>
            )}

            {!isFinalizedCheckoutSummary && discountPromotionGroups.map(promotion => (
                <Fragment key={promotion.key}>
                    <GridItem {...styles.labelGridItem} width={6}>
                        <Typography {...styles.discountPromotionLabel} data-test-selector="cartTotal_orderPromotionName">
                            {`${translate("Discounts")} • ${promotion.label}`}
                        </Typography>
                    </GridItem>
                    <GridItem {...styles.valueGridItem} width={6}>
                        <Typography {...styles.discountPromotionValue} data-test-selector="cartTotal_orderPromotionValue">
                            -
                            <LocalizedCurrency amount={promotion.amount} currencySymbol={cart.currencySymbol} />
                        </Typography>
                    </GridItem>
                </Fragment>
            ))}
            {currentPage !== "CheckoutShippingPage" &&
            showTaxAndShipping &&
            cart.shippingAndHandlingDisplay.length > 0 &&
            (isUkWebsite || fulfillmentMethod !== FulfillmentMethod.PickUp) ? (
                <>
                    <GridItem {...styles.labelGridItem}>
                        <Typography {...styles.shippingAndHandlingLabel}>
                            {translate("Shipping \& Handling")}
                        </Typography>
                    </GridItem>
                    <GridItem {...styles.valueGridItem}>
                        <Typography
                            {...styles.shippingAndHandlingValue}
                            data-test-selector="cartTotal_shippingAndHandlingAmount"
                        >
                            <LocalizedCurrency amount={baseShippingAndHandling} currencySymbol={cart.currencySymbol} />
                        </Typography>
                    </GridItem>
                </>
            ) : null}
            {currentPage !== "CheckoutShippingPage" && showTaxAndShipping && whiteGloveTotal > 0 ? (
                <>
                    <GridItem {...styles.labelGridItem}>
                        <Typography {...styles.whiteGloveLabel}>{translate("White Glove")}</Typography>
                    </GridItem>
                    <GridItem {...styles.valueGridItem}>
                        <Typography {...styles.whiteGloveValue} data-test-selector="cartTotal_whiteGloveAmount">
                            <LocalizedCurrency amount={whiteGloveTotal} currencySymbol={cart.currencySymbol} />
                        </Typography>
                    </GridItem>
                </>
            ) : null}
            {!isFinalizedCheckoutSummary && visibleShippingPromotionGroups.map(promotion => (
                <Fragment key={promotion.key}>
                    <GridItem {...styles.labelGridItem} width={7}>
                        <Typography {...styles.promotionLabel} data-test-selector="cartTotal_shippingPromotionName">
                            {`${translate("Shipping Promo")} • ${promotion.label}`}
                        </Typography>
                    </GridItem>
                    <GridItem {...styles.valueGridItem} width={5}>
                        {showShippingPromotionAsMessage ? (
                            <Typography
                                {...styles.shippingPromotionMessage}
                                data-test-selector="cartTotal_shippingPromotionMessage"
                            >
                                {`${siteMessage("Promotion_ShippingDiscountCalculatedAtCheckout")}${
                                    isCheckoutShippingAddressStep ? "*" : ""
                                }`}
                            </Typography>
                        ) : (
                            <Typography
                                {...styles.promotionValue}
                                data-test-selector="cartTotal_shippingPromotionValue"
                            >
                                -
                                <LocalizedCurrency amount={promotion.amount} currencySymbol={cart.currencySymbol} />
                            </Typography>
                        )}
                    </GridItem>
                </Fragment>
            ))}
            {showTaxAndShipping &&
                allowTaxAmountOnPage &&
                isUkWebsite &&
                fulfillmentMethod === FulfillmentMethod.PickUp &&
                !allowUkTaxAmount && (
                    <>
                        <GridItem {...styles.labelGridItem} width={3}>
                            <Typography {...styles.taxLabel}>
                                {enableVat ? translate("VAT") : translate("Tax")}
                            </Typography>
                        </GridItem>
                        <GridItem {...styles.valueGridItem} width={9}>
                            <Typography {...styles.taxMessageNote}>{siteMessage("Tax_Calculated_Next_Step")}</Typography>
                        </GridItem>
                    </>
                )}
            {showTaxAndShipping &&
                allowTaxAmountOnPage &&
                allowTaxAmountByFulfillment &&
                cart.totalTaxDisplay.length > 0 &&
                cart.customerOrderTaxes!.length === 0 && (
                    <>
                        <GridItem {...styles.labelGridItem}>
                            <Typography {...styles.taxLabel}>
                                {enableVat ? translate("VAT") : translate("Tax")}
                            </Typography>
                        </GridItem>
                        <GridItem {...styles.valueGridItem}>
                            <Typography {...styles.taxValue} data-test-selector="cartTotal_totalTaxDisplay">
                                {cart.totalTaxDisplay}
                            </Typography>
                        </GridItem>
                    </>
                )}
            {showTaxAndShipping &&
                allowTaxAmountOnPage &&
                allowTaxAmountByFulfillment &&
                [...cart.customerOrderTaxes!]
                    .sort((a, b) => a.sortOrder - b.sortOrder)
                    .map(tax => {
                        const key = `${tax.taxCode}_${tax.sortOrder}`;
                        return (
                            <Fragment key={key}>
                                <GridItem {...styles.labelGridItem}>
                                    <Typography {...styles.taxLabel}>{tax.taxDescription || translate("Tax")}</Typography>
                                </GridItem>
                                <GridItem {...styles.valueGridItem}>
                                    <Typography {...styles.taxValue}>{tax.taxAmountDisplay}</Typography>
                                </GridItem>
                            </Fragment>
                        );
                    })}

            {showFinalizedPromotionBlock && (
                <GridItem {...styles.valueGridItem} width={12}>
                    <StyledWrapper {...styles.horizontalLineStyles} />
                </GridItem>
            )}
            {isFinalizedCheckoutSummary && discountPromotionGroups.map(promotion => (
                <Fragment key={promotion.key}>
                    <GridItem {...styles.labelGridItem} width={6}>
                        <Typography {...styles.discountPromotionLabel} data-test-selector="cartTotal_orderPromotionName">
                            {`${translate("Discounts")} • ${promotion.label}`}
                        </Typography>
                    </GridItem>
                    <GridItem {...styles.valueGridItem} width={6}>
                        <Typography {...styles.discountPromotionValue} data-test-selector="cartTotal_orderPromotionValue">
                            -
                            <LocalizedCurrency amount={promotion.amount} currencySymbol={cart.currencySymbol} />
                        </Typography>
                    </GridItem>
                </Fragment>
            ))}
            {isFinalizedCheckoutSummary && visibleShippingPromotionGroups.map(promotion => (
                <Fragment key={promotion.key}>
                    <GridItem {...styles.labelGridItem} width={7}>
                        <Typography {...styles.promotionLabel} data-test-selector="cartTotal_shippingPromotionName">
                            {`${translate("Shipping Promo")} • ${promotion.label}`}
                        </Typography>
                    </GridItem>
                    <GridItem {...styles.valueGridItem} width={5}>
                        <Typography {...styles.promotionValue} data-test-selector="cartTotal_shippingPromotionValue">
                            -
                            <LocalizedCurrency amount={promotion.amount} currencySymbol={cart.currencySymbol} />
                        </Typography>
                    </GridItem>
                </Fragment>
            ))}
            {showFinalizedPromotionBlock && (
                <GridItem {...styles.valueGridItem} width={12}>
                    <StyledWrapper {...styles.horizontalLineStyles} />
                </GridItem>
            )}

            {showEstimatedTotal && (
                <>
                    <GridItem {...styles.valueGridItem} width={12}>
                        <StyledWrapper {...styles.horizontalLineStyles} />
                    </GridItem>
                    <GridItem {...styles.estimatedTotalGridItem}>
                        <Typography {...styles.estimatedTotalLabel}>{translate("Estimated Total")}</Typography>
                        <Typography {...styles.estimatedTotalHelperText}>
                            {translate("Before shipping and tax")}
                        </Typography>
                    </GridItem>
                    <GridItem {...styles.totalValueGridItem}>
                        <Typography
                            {...styles.estimatedTotalValue}
                            data-test-selector="cartTotal_estimatedTotal"
                        >
                            <LocalizedCurrency amount={estimatedTotal} currencySymbol={cart.currencySymbol} />
                        </Typography>
                    </GridItem>
                </>
            )}

            {showTaxAndShipping && !(isCheckoutShippingAddressStep && showEstimatedTotal) && (
                <>
                    <GridItem {...styles.totalGridItem}>
                        <Typography {...styles.totalLabel}>{translate("Total")}</Typography>
                    </GridItem>
                    <GridItem {...styles.totalValueGridItem}>
                        <Typography {...styles.totalValue} data-test-selector="cartTotal_orderGrandTotalDisplay">
                            {currentPage === "CheckoutShippingPage" ? (
                                <LocalizedCurrency
                                    amount={
                                        (cart.orderGrandTotal || 0) -
                                        (cart.shippingAndHandling || 0) -
                                        (cart.totalTax || 0)
                                    }
                                    currencySymbol={cart.currencySymbol}
                                />
                            ) : RollpadServiceCount > 0 && typeof (cart as any).orderGrandTotal === "number" ? (
                                <LocalizedCurrency
                                    amount={(cart as any).orderGrandTotal}
                                    currencySymbol={cart.currencySymbol}
                                />
                            ) : (
                                cart.orderGrandTotalDisplay
                            )}
                        </Typography>
                    </GridItem>
                </>
            )}
            {(showShippingPromotionCalculationNote || showTaxCalculationNote) && (
                <GridItem {...styles.valueGridItem} width={12}>
                    <Typography {...styles.shippingPromotionCalculationNote}>* {pendingCalculationNote}</Typography>
                </GridItem>
            )}

        </GridContainer>
    );
};

export default connect(mapStateToProps)(CartTotalDisplay);
